import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Section, Card, KPI, Table, ChartTip, useMoney, shortDate } from "./shared";

const TOP_TABS = [
  { key: "top_units", label: "Más vendidos (piezas)" },
  { key: "top_revenue", label: "Más dinero" },
  { key: "top_profit", label: "Más utilidad" },
];

export default function SalesSection({ r }) {
  const { fmt, compact } = useMoney();
  const b = r.behavior;
  const [tab, setTab] = useState("top_units");
  const daily = b.daily.map((d) => ({ ...d, label: String(d.day) }));
  const maxDay = Math.max(...b.weekday.map((w) => w.count), 0);
  const rows = b[tab] || [];

  return (
    <Section id="ventas" num={2} title="Comportamiento de ventas" desc="Distribución de la venta a lo largo del mes: días, semanas y productos que más aportaron.">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KPI label="Venta total" value={fmt(b.sales_total_mxn)} tone="primary" small />
        <KPI label="Transacciones" value={b.transactions} small />
        <KPI label="Piezas vendidas" value={b.units_sold} small />
        <KPI label="Ticket promedio" value={fmt(b.avg_ticket_mxn)} small />
        <KPI label="Venta promedio diaria" value={fmt(b.avg_daily_mxn)} sub={`${b.elapsed_days} día(s)${r.is_current_month ? " transcurridos" : ""}`} small />
        <KPI label="Mejor día" value={b.best_day && b.best_day.total_mxn > 0 ? shortDate(b.best_day.date) : "—"} sub={b.best_day ? `${fmt(b.best_day.total_mxn)} · ${b.best_day.count} nota(s)` : ""} tone="good" small testid="kpi-best-day" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <Card title="Venta por día" desc="Barras = venta del día. Pasa el cursor para ver notas y piezas." className="xl:col-span-8" testid="mr-daily-chart">
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#71717a" }} axisLine={false} tickLine={false} interval={daily.length > 20 ? 1 : 0} />
                <YAxis tick={{ fontSize: 10, fill: "#71717a" }} axisLine={false} tickLine={false} width={58} tickFormatter={(v) => compact(v)} />
                <Tooltip cursor={{ fill: "#f4f4f5" }} content={<ChartTip fmt={(v, k) => (k === "total_mxn" ? fmt(v) : v)} />} labelFormatter={(l) => `Día ${l}`} />
                <Bar dataKey="total_mxn" name="Venta" fill="#002FA7" radius={[2, 2, 0, 0]}>
                  {daily.map((d) => <Cell key={d.date} fill={b.best_day && d.date === b.best_day.date && d.total_mxn > 0 ? "#00873D" : "#002FA7"} />)}
                </Bar>
                <Bar dataKey="count" name="Notas" fill="#FFC000" radius={[2, 2, 0, 0]} hide />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Semanas del mes" desc="Semana con mayor y menor venta." className="xl:col-span-4" testid="mr-weeks">
          <ul className="space-y-1.5 text-xs">
            {b.weeks.map((w) => {
              const best = b.best_week && w.label === b.best_week.label && w.total_mxn > 0;
              const worst = b.worst_week && w.label === b.worst_week.label && !best;
              const max = Math.max(...b.weeks.map((x) => x.total_mxn), 1);
              return (
                <li key={w.label} className={`p-2 border rounded-sm ${best ? "border-emerald-200 bg-emerald-50" : worst ? "border-[#E0000F]/20 bg-[#E0000F]/5" : "border-zinc-100"}`} data-testid={`mr-week-${w.label}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-zinc-800">{w.label} <span className="text-zinc-400 font-normal">· días {w.range}</span></span>
                    <span className="numeric font-semibold">{fmt(w.total_mxn)} <span className="text-zinc-400 font-normal">· {w.count}</span></span>
                  </div>
                  <div className="h-1 bg-zinc-100 rounded-full mt-1.5 overflow-hidden"><div className={`h-full ${best ? "bg-emerald-500" : worst ? "bg-[#E0000F]" : "bg-[#002FA7]"}`} style={{ width: `${(w.total_mxn / max) * 100}%` }} /></div>
                  {(best || worst) && <div className={`text-[10px] mt-1 font-semibold ${best ? "text-emerald-700" : "text-[#E0000F]"}`}>{best ? "Semana con mayor venta" : "Semana con menor venta"}</div>}
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <Card title="Días de la semana" desc="Cantidad de notas por día de la semana (y venta)." className="xl:col-span-4" testid="mr-weekday">
          <ul className="space-y-1.5 text-xs">
            {b.weekday.map((w) => (
              <li key={w.key} className="flex items-center gap-2">
                <span className="w-20 text-zinc-700">{w.label}</span>
                <div className="flex-1 h-4 bg-zinc-100 rounded-sm overflow-hidden relative">
                  <div className="h-full bg-[#002FA7]/80" style={{ width: `${maxDay ? (w.count / maxDay) * 100 : 0}%` }} />
                </div>
                <span className="w-8 text-right numeric font-semibold">{w.count}</span>
                <span className="w-20 text-right numeric text-zinc-500">{compact(w.total_mxn)}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card className="xl:col-span-8" testid="mr-top-products">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="text-xs font-semibold text-zinc-800">Top productos del mes</div>
            <div className="inline-flex border border-zinc-200 rounded-sm overflow-hidden text-xs">
              {TOP_TABS.map((t) => (
                <button key={t.key} type="button" onClick={() => setTab(t.key)} className={`px-3 py-1.5 font-semibold ${tab === t.key ? "bg-[#002FA7] text-white" : "text-zinc-600 hover:bg-zinc-50"}`} data-testid={`mr-top-tab-${t.key}`}>{t.label}</button>
              ))}
            </div>
          </div>
          <Table
            rows={rows}
            keyFn={(x, i) => x.product_id || `${x.name}-${i}`}
            empty="Sin productos vendidos en el mes."
            cols={[
              { key: "rank", label: "#", render: (_, i) => <span className="text-zinc-400 numeric">{i + 1}</span> },
              { key: "name", label: "Producto", render: (x) => <div><div className="font-medium text-zinc-900">{x.name}</div><div className="text-[10px] text-zinc-500">{x.category}</div></div> },
              { key: "units", label: "Piezas", align: "right" },
              { key: "revenue_mxn", label: "Venta", align: "right", render: (x) => fmt(x.revenue_mxn) },
              { key: "profit_mxn", label: "Utilidad", align: "right", render: (x) => x.has_cost ? <span className={x.profit_mxn >= 0 ? "text-emerald-700" : "text-[#E0000F]"}>{fmt(x.profit_mxn)}</span> : <span className="text-zinc-400 text-[11px]">sin costo</span> },
            ]}
          />
        </Card>
      </div>
    </Section>
  );
}
